/**
 * ArchiveContext - Platform-Agnostic Archive Provider
 *
 * Provides a unified interface for archive operations across platforms:
 * - Electron: Direct IPC to local PostgreSQL + pgvector + Ollama
 * - Web: HTTP API to Cloudflare D1 + Vectorize + Workers AI
 *
 * Key features:
 * - Platform detection (electron vs web)
 * - Service status management
 * - Setup state tracking for graceful onboarding
 * - Unified API regardless of backend
 *
 * @module @humanizer/studio/contexts/ArchiveContext
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, type ReactNode } from 'react';
import type { ArchiveNode, ArchiveStats, ArchiveThread, ArchiveSource } from './ApiContext';
import { createLogger } from '../utils/debug';

const log = createLogger('ArchiveCtx');

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Archive connection status
 */
export type ArchiveStatus = 'checking' | 'connected' | 'setup-required' | 'error';

/**
 * Platform type
 */
export type ArchivePlatform = 'electron' | 'web';

/**
 * Service availability for Electron
 */
export interface ElectronServices {
  postgres: {
    available: boolean;
    configured: boolean;
    error?: string;
  };
  ollama: {
    available: boolean;
    configured: boolean;
    models?: string[];
    error?: string;
  };
}

/**
 * Archive configuration for Electron
 */
export interface ElectronArchiveConfig {
  postgres: {
    host: string;
    port: number;
    database: string;
    user: string;
    password?: string;
  };
  ollama: {
    url: string;
  };
}

/**
 * Archive context state
 */
export interface ArchiveState {
  platform: ArchivePlatform;
  status: ArchiveStatus;
  stats: ArchiveStats | null;
  error: string | null;
  services: ElectronServices | null;
  config: ElectronArchiveConfig | null;
}

/**
 * Query options for browsing archive nodes
 */
export interface ArchiveBrowseOptions {
  sourceType?: string;
  threadRootId?: string;
  parentNodeId?: string;
  hierarchyLevel?: number;
  authorRole?: 'user' | 'assistant' | 'system';
  limit?: number;
  offset?: number;
  orderBy?: 'createdAt' | 'sourceCreatedAt' | 'importedAt' | 'wordCount';
  orderDir?: 'asc' | 'desc';
}

/**
 * Archive provider interface - unified across platforms
 */
export interface ArchiveProvider {
  // Status
  checkStatus(): Promise<{ connected: boolean; stats?: ArchiveStats; error?: string }>;

  // Browse
  browse(options?: ArchiveBrowseOptions): Promise<{ nodes: ArchiveNode[]; total: number; hasMore: boolean }>;
  getSources(): Promise<{ sources: ArchiveSource[]; total: number }>;
  getThreads(options?: { sourceType?: string; limit?: number; offset?: number; dateFrom?: string; dateTo?: string; sortBy?: 'quality' | 'date'; hideNoise?: boolean }): Promise<{ threads: ArchiveThread[]; total: number; hasMore: boolean }>;
  getThread(threadId: string): Promise<{ threadId: string; nodes: ArchiveNode[]; count: number }>;
  getNode(nodeId: string): Promise<{ node: ArchiveNode }>;
  getStats(): Promise<ArchiveStats>;

  // Search
  search?(query: string, options?: { sources?: string[]; limit?: number }): Promise<{ results: any[]; total: number }>;

  // Configuration (Electron only)
  configure?(config: ElectronArchiveConfig): Promise<{ success: boolean; error?: string }>;
  getServices?(): Promise<ElectronServices>;
}

/**
 * Archive context value
 */
export interface ArchiveContextValue extends ArchiveState {
  // Actions
  refresh: () => Promise<void>;
  configure: (config: ElectronArchiveConfig) => Promise<{ success: boolean; error?: string }>;

  // Provider (for direct access when needed)
  provider: ArchiveProvider;
}

// ═══════════════════════════════════════════════════════════════════════════
// ELECTRON PROVIDER - Direct IPC to local services
// ═══════════════════════════════════════════════════════════════════════════

function createElectronProvider(): ArchiveProvider {
  const electronArchive = window.electronArchive;

  if (!electronArchive) {
    throw new Error('Electron archive API not available');
  }

  return {
    async checkStatus() {
      try {
        const result = await electronArchive.status();
        return {
          connected: result.connected,
          stats: result.stats,
          error: result.error,
        };
      } catch (error) {
        return {
          connected: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    },

    async browse(options) {
      return electronArchive.query('queryNodes', [options || {}]) as Promise<{ nodes: ArchiveNode[]; total: number; hasMore: boolean }>;
    },

    async getSources() {
      return electronArchive.query('getSources', []) as Promise<{ sources: ArchiveSource[]; total: number }>;
    },

    async getThreads(options) {
      return electronArchive.query('getThreads', [options || {}]) as Promise<{ threads: ArchiveThread[]; total: number; hasMore: boolean }>;
    },

    async getThread(threadId) {
      return electronArchive.query('getThread', [threadId]) as Promise<{ threadId: string; nodes: ArchiveNode[]; count: number }>;
    },

    async getNode(nodeId) {
      return electronArchive.query('getNode', [nodeId]) as Promise<{ node: ArchiveNode }>;
    },

    async getStats() {
      return electronArchive.query('getStats', []) as Promise<ArchiveStats>;
    },

    async search(query, options) {
      return electronArchive.query('search', [query, options || {}]) as Promise<{ results: any[]; total: number }>;
    },

    async configure(config) {
      try {
        const result = await electronArchive.configure(config);
        return { success: result.success, error: result.error };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Configuration failed',
        };
      }
    },

    async getServices() {
      return electronArchive.getServices();
    },
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// CLOUD PROVIDER - HTTP API to Cloudflare Workers
// ═══════════════════════════════════════════════════════════════════════════

function createCloudProvider(apiClient: any): ArchiveProvider {
  return {
    async checkStatus() {
      try {
        const stats = await apiClient.archive.getStats();
        return { connected: true, stats };
      } catch (error) {
        // Check for specific error types
        if (error instanceof Error) {
          if (error.message.includes('401') || error.message.includes('Unauthorized')) {
            return { connected: false, error: 'Authentication required' };
          }
          if (error.message.includes('503') || error.message.includes('unavailable')) {
            return { connected: false, error: 'Service temporarily unavailable' };
          }
        }
        return {
          connected: false,
          error: error instanceof Error ? error.message : 'Connection failed',
        };
      }
    },

    async browse(options) {
      return apiClient.archive.browse(options);
    },

    async getSources() {
      return apiClient.archive.getSources();
    },

    async getThreads(options) {
      return apiClient.archive.getThreads(options);
    },

    async getThread(threadId) {
      return apiClient.archive.getThread(threadId);
    },

    async getNode(nodeId) {
      return apiClient.archive.getNode(nodeId);
    },

    async getStats() {
      return apiClient.archive.getStats();
    },

    // Cloud doesn't need configure - it's handled via auth
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// CONTEXT
// ═══════════════════════════════════════════════════════════════════════════

const ArchiveContext = createContext<ArchiveContextValue | null>(null);

/**
 * Hook to access archive context
 */
export function useArchive(): ArchiveContextValue {
  const ctx = useContext(ArchiveContext);
  if (!ctx) {
    throw new Error('useArchive must be used within ArchiveProvider');
  }
  return ctx;
}

/**
 * Hook to check if archive is available (doesn't throw if not in provider)
 */
export function useArchiveOptional(): ArchiveContextValue | null {
  return useContext(ArchiveContext);
}

// ═══════════════════════════════════════════════════════════════════════════
// PROVIDER
// ═══════════════════════════════════════════════════════════════════════════

interface ArchiveProviderProps {
  /** API client for cloud provider (from ApiContext) */
  apiClient?: any;
  children: ReactNode;
}

/**
 * Archive provider component
 *
 * Detects platform and initializes appropriate provider:
 * - Electron: Uses IPC to local PostgreSQL/Ollama
 * - Web: Uses HTTP API to cloud backend
 */
export function ArchiveProvider({ apiClient, children }: ArchiveProviderProps) {
  // Detect platform
  const platform: ArchivePlatform = useMemo(() => {
    const isElectron = typeof window !== 'undefined' && !!window.isElectron;
    return isElectron ? 'electron' : 'web';
  }, []);

  // State
  const [status, setStatus] = useState<ArchiveStatus>('checking');
  const [stats, setStats] = useState<ArchiveStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [services, setServices] = useState<ElectronServices | null>(null);
  const [config, setConfig] = useState<ElectronArchiveConfig | null>(null);

  // Create provider based on platform
  const provider = useMemo<ArchiveProvider>(() => {
    if (platform === 'electron') {
      try {
        return createElectronProvider();
      } catch {
        // Electron API not available - fall back to cloud
        log.warn('Electron archive API not available, falling back to cloud');
        if (!apiClient) {
          // Return a stub that reports setup required
          return {
            async checkStatus() {
              return { connected: false, error: 'Archive not configured' };
            },
            async browse() {
              throw new Error('Archive not configured');
            },
            async getSources() {
              throw new Error('Archive not configured');
            },
            async getThreads() {
              throw new Error('Archive not configured');
            },
            async getThread() {
              throw new Error('Archive not configured');
            },
            async getNode() {
              throw new Error('Archive not configured');
            },
            async getStats() {
              throw new Error('Archive not configured');
            },
          };
        }
        return createCloudProvider(apiClient);
      }
    }
    // Web platform
    if (!apiClient) {
      // Return a stub that reports auth required
      return {
        async checkStatus() {
          return { connected: false, error: 'Authentication required' };
        },
        async browse() {
          throw new Error('Authentication required');
        },
        async getSources() {
          throw new Error('Authentication required');
        },
        async getThreads() {
          throw new Error('Authentication required');
        },
        async getThread() {
          throw new Error('Authentication required');
        },
        async getNode() {
          throw new Error('Authentication required');
        },
        async getStats() {
          throw new Error('Authentication required');
        },
      };
    }
    return createCloudProvider(apiClient);
  }, [platform, apiClient]);

  // Check status on mount and when provider changes
  const refresh = useCallback(async () => {
    setStatus('checking');
    setError(null);

    try {
      // ELECTRON: Use IPC exclusively, never fall back to cloud
      if (platform === 'electron') {
        if (!provider.getServices) {
          setStatus('setup-required');
          return;
        }

        try {
          const servicesResult = await provider.getServices();
          setServices(servicesResult);

          // If postgres not configured, show setup
          if (!servicesResult.postgres.configured) {
            setStatus('setup-required');
            return;
          }

          // Postgres is configured, check if actually connected
          if (!servicesResult.postgres.available) {
            setStatus('error');
            setError(servicesResult.postgres.error || 'PostgreSQL not running');
            return;
          }

          // Connected!
          setStatus('connected');
          return;
        } catch (err) {
          setStatus('setup-required');
          setError('Failed to check local services');
          return;
        }
      }

      // WEB: Use cloud API
      const result = await provider.checkStatus();

      if (result.connected) {
        setStatus('connected');
        setStats(result.stats || null);
        setError(null);
      } else if (result.error?.includes('Authentication required')) {
        setStatus('setup-required');
        setError(result.error);
      } else if (result.error?.includes('not configured')) {
        setStatus('setup-required');
        setError(result.error);
      } else {
        setStatus('error');
        setError(result.error || 'Failed to connect to archive');
      }
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  }, [platform, provider]);

  // Initial status check
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Configure handler (Electron only)
  const configure = useCallback(
    async (newConfig: ElectronArchiveConfig): Promise<{ success: boolean; error?: string }> => {
      if (platform !== 'electron' || !provider.configure) {
        return { success: false, error: 'Configuration only available on Electron' };
      }

      const result = await provider.configure(newConfig);

      if (result.success) {
        setConfig(newConfig);
        // Refresh status after configuration
        await refresh();
      }

      return result;
    },
    [platform, provider, refresh]
  );

  // Context value
  const value = useMemo<ArchiveContextValue>(
    () => ({
      platform,
      status,
      stats,
      error,
      services,
      config,
      refresh,
      configure,
      provider,
    }),
    [platform, status, stats, error, services, config, refresh, configure, provider]
  );

  return <ArchiveContext.Provider value={value}>{children}</ArchiveContext.Provider>;
}
